"use client";

import { useState } from "react";
import { ArrowRight, Check, Folder } from "lucide-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ClipArtCategory } from "@/interfaces/clip-art.interface";
import ClipArtManagement from "@/components/library/ClipArtManagement";

interface Props {
  source: ClipArtCategory;
  visible: boolean;
  loading?: boolean;
  onHide: () => void;
  onConfirm: (destinationId: string | null, cloneName: string) => void;
}

type Destination = ClipArtCategory | null;

export default function CloneDestDialog({
  source,
  visible,
  loading,
  onHide,
  onConfirm,
}: Props) {
  const [selected, setSelected] = useState<Destination>(null);
  const [cloneName, setCloneName] = useState(`${source.name} (cloned)`);

  const trimmedCloneName = cloneName.trim();

  const onConfirmClick = () => {
    const destinationId = selected?._id ?? null;
    onConfirm(destinationId, trimmedCloneName);
  };

  return (
    <Dialog
      header="Clone into"
      visible={visible}
      focusOnShow={false}
      className="dialog"
      style={{ width: "70vw", height: "70vh" }}
      headerStyle={{
        padding: 16,
        paddingBottom: 0,
        overflow: "hidden",
      }}
      contentStyle={{ padding: 0, display: "flex", flexDirection: "column" }}
      footer={
        <div className="flex items-center justify-end border-t border-gray-300 p-4">
          <Button
            severity="info"
            style={{ height: 36, margin: 0, padding: "6px 10px", gap: 4 }}
            label="Clone"
            loading={loading}
            disabled={trimmedCloneName === ""}
            icon={<Check size={18} />}
            onClick={onConfirmClick}
          />
        </div>
      }
      onHide={onHide}
    >
      <div className="flex min-w-0 w-full items-center gap-2 text-md text-gray-600 p-4">
        <div className="flex w-full items-center gap-2">
          <span className="font-medium text-gray-700 whitespace-nowrap">
            Cloned Name:
          </span>
          <div className="flex min-w-0 w-full cursor-text items-center gap-1.5 rounded-md border px-2 py-1 transition-colors border-gray-300 focus-within:border-gray-300 focus-within:bg-white">
            <Folder size={18} className="shrink-0 text-gray-400" />
            <input
              value={cloneName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCloneName(e.target.value)
              }
              onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                const target = e.currentTarget;
                setTimeout(() => {
                  const len = target.value.length;
                  target.setSelectionRange(len, len);
                  target.scrollLeft = target.scrollWidth;
                });
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" || e.key === "Escape") {
                  e.currentTarget.blur();
                }
              }}
              placeholder={`${source.name} (cloned)`}
              className="min-w-0 flex-1 cursor-text bg-transparent p-0 text-left font-medium text-gray-700 outline-none"
              style={{
                textOverflow: "ellipsis",
                border: "none",
                boxShadow: "none",
              }}
            />
          </div>
        </div>

        {selected && (
          <div className="flex items-center gap-2 w-full overflow-auto">
            <ArrowRight size={18} className="shrink-0 text-gray-400" />
            <div
              className="min-w-0 flex items-center gap-2 flex-1 truncate rounded-md border border-transparent bg-gray-100 px-2 py-1 font-medium text-gray-700"
              title={selected.name}
            >
              <Folder size={18} className="shrink-0 text-gray-400" />
              {selected.name}
            </div>
          </div>
        )}
      </div>
      <ClipArtManagement
        disableClone
        onSelectCategory={(cat) =>
          setSelected(cat?._id === "" ? null : (cat ?? null))
        }
      />
    </Dialog>
  );
}
